<template>
  <div class="model-creator">
    <!-- Method Selection Screen -->
    <div v-if="currentScreen === 'selection'" class="selection-screen">
      <h1>Warhall Portal</h1>
      <p class="subtitle">Create Your 3D Models</p>
      
      <div class="method-cards">
        <div v-if="false" class="method-card active" @click="selectMethod('image')">
          <div class="card-icon">📷</div>
          <h3>From Image</h3>
          <p>Upload or capture a photo</p>
          <button class="select-btn">Select</button>
        </div>
        
        <div class="method-card active" @click="selectMethod('scan')">
          <div class="card-icon">📷</div>
          <h3>Scan Miniature</h3>
          <p>Capture from multiple angles</p>
          <button class="select-btn">Select</button>
        </div>
      </div>
      
      <div class="test-mode-section">
        <button class="test-mode-btn" @click="selectMethod('text')">
          ✍️ Text Prompt
        </button>
        <button class="test-mode-btn" @click="selectMethod('glb')">
          📦 Upload Model
        </button>
        <button class="test-mode-btn" @click="showTestInput = !showTestInput">
          🔍 Load Task ID
        </button>
        <div v-if="showTestInput" class="test-input-group">
          <input 
            type="text" 
            v-model="existingTaskId" 
            placeholder="Paste Generation Task ID here"
            class="task-id-input"
          />
          <button 
            class="load-task-btn" 
            @click="loadExistingTask" 
            :disabled="!existingTaskId || isLoading"
          >
            Load Model
          </button>
        </div>
      </div>
    </div>

    <!-- GLB Upload Screen -->
    <div v-if="currentScreen === 'glbUpload'" class="glb-upload-screen">
      
      <h1>Upload 3D Model</h1>
      <p class="subtitle">Upload a file from your computer, or load from a URL</p>
      
      <div v-if="!uploadedGlbFile && !glbUrlInput && !showUrlInputScreen" class="glb-upload-options">
        <div class="upload-area upload-area--url" @click="showUrlInputScreen = true">
          <div class="upload-icon">🔗</div>
          <p>Load from URL</p>
          <small>Paste a public link to a GLB, FBX or OBJ file</small>
        </div>
      </div>

      <!-- URL input screen -->
      <div v-if="showUrlInputScreen && !uploadedGlbFile && !glbUrlInput" class="url-input-screen">
        <small style="margin-top:12px;display:block;">3D Model URL</small>
        <div class="url-input-row">
          <input
            ref="glbUrlField"
            type="url"
            v-model="glbUrlDraft"
            placeholder="https://example.com/model.glb"
            class="url-field"
            @keydown.enter="confirmGlbUrl"
          />
        </div>
        <template v-if="!/\.(glb|gltf|fbx)(\?.*)?$/i.test(glbUrlDraft.trim())">
          <small style="margin-top:12px;display:block;">Texture URL (optional, for OBJ models)</small>
          <div class="url-input-row">
            <input
              ref="glbTexUrlField"
              type="url"
              v-model="glbTexUrlDraft"
              placeholder="https://example.com/texture.jpg"
              class="url-field"
              @keydown.enter="confirmGlbUrl"
            />
          </div>
        </template>
        <div class="url-input-row" style="margin-top:12px;">
          <button class="change-btn" @click="showUrlInputScreen = false">Back</button>
          <button class="url-load-btn" :disabled="!glbUrlDraft.trim()" @click="confirmGlbUrl">Load</button>
        </div>
      </div>

      <div class="screen-nav">
        <button v-if="!showUrlInputScreen" class="nav-btn nav-btn--back" @click="goBack">Back</button>
      </div>
    </div>

    <div v-if="currentScreen === 'imageInput'" class="image-input-screen">
      
      <h1>Upload or Capture Image</h1>
      <p class="subtitle">Take a clear photo of your miniature against a plain background</p>
      
      <!-- Quality Preset Toggle (hidden until re-enabled) -->
      <div v-if="false" class="quality-preset-selector">
        <label>Quality Preset:</label>
        <div class="preset-options">
          <button 
            :class="['preset-btn', { active: qualityPreset === 'standard' }]"
            @click="qualityPreset = 'standard'"
          >
            ⚡ Standard
            <small>Fast &amp; Balanced</small>
          </button>
          <button 
            :class="['preset-btn', { active: qualityPreset === 'high_quality' }]"
            @click="qualityPreset = 'high_quality'"
          >
            ✨ High Quality
            <small>PBR Textures</small>
          </button>
        </div>
      </div>

      <div v-if="!capturedImage" class="input-options">
        <div class="upload-area" @click="triggerFileUpload">
          <div class="upload-icon">📁</div>
          <p>Click to upload image</p>
          <small>JPG, PNG, or WebP (max 10MB)</small>
          <input 
            ref="fileInput" 
            type="file" 
            accept="image/jpeg,image/png,image/webp" 
            @change="handleFileUpload" 
            style="display: none"
          />
        </div>
        
        <div v-if="isMobile" class="camera-area" @click="openCamera">
          <div class="camera-icon">📸</div>
          <p>Use Camera</p>
          <small>Take a photo with your device</small>
        </div>
      </div>
      
      <!-- Camera Preview (only shown when camera is active) -->
      <div v-if="cameraActive" class="camera-preview">
        <video ref="videoElement" autoplay playsinline></video>
        <button class="capture-btn" @click="capturePhoto">Capture Photo</button>
        <button class="cancel-camera-btn" @click="closeCamera">Cancel</button>
      </div>
      
      <!-- Image Preview -->
      <div v-if="capturedImage" class="image-preview">
        <img :src="capturedImage" alt="Preview" />
        <div class="preview-actions">
          <button class="change-btn" @click="clearImage">Change Image</button>
        </div>
      </div>

      <div class="screen-nav">
        <button class="nav-btn nav-btn--back" @click="goBack">Back</button>
        <a href="/store#Coins" target="_blank" class="nav-btn nav-btn--next" style="text-decoration:none;"><img src="/Images/Warhallcoin_128.png" style="height: 20px; vertical-align: middle; margin-right: 5px;" /> Buy</a>
        <button v-if="capturedImage" class="nav-btn nav-btn--next" @click="createModel" :disabled="isLoading || userCoins < 1">
          <span v-if="isLoading">Creating...</span>
          <span v-else-if="userCoins < 1">❌ No Coins</span>
          <span v-else><img src="/Images/Warhallcoin_128.png" style="height: 20px; vertical-align: middle; margin-right: 5px;" /> Create 3D Model</span>
        </button>
      </div>
    </div>

    <!-- Text Prompt Screen -->
    <div v-if="currentScreen === 'textInput'" class="text-input-screen">
      <h1>Describe Your Model</h1>
      <p class="subtitle">Describe what you want to create in detail</p>

      <div v-if="false" class="quality-preset-selector">
        <label>Quality Preset:</label>
        <div class="preset-options">
          <button :class="['preset-btn', { active: qualityPreset === 'standard' }]" @click="qualityPreset = 'standard'">
            ⚡ Standard
            <small>Fast &amp; Balanced</small>
          </button>
          <button :class="['preset-btn', { active: qualityPreset === 'high_quality' }]" @click="qualityPreset = 'high_quality'">
            ✨ High Quality
            <small>PBR Textures</small>
          </button>
        </div>
      </div>

      <div class="prompt-area">
        <textarea
          v-model="textPrompt"
          placeholder="A medieval warrior in full plate armor, holding a sword and shield, heroic pose..."
          maxlength="1024"
          class="prompt-input"
        ></textarea>
        <small class="char-count">{{ textPrompt.length }}/1024 characters</small>
      </div>

      <div v-if="isLoading" class="loading-section">
        <div class="progress-bar"><div class="progress-fill" :style="{ width: progress + '%' }"></div></div>
        <p>{{ loadingMessage }}</p>
      </div>

      <div class="screen-nav">
        <button class="nav-btn nav-btn--back" @click="goBack">Back</button>
        <a href="/store#Coins" target="_blank" class="nav-btn nav-btn--next" style="text-decoration:none;"><img src="/Images/Warhallcoin_128.png" style="height: 20px; vertical-align: middle; margin-right: 5px;" /> Buy</a>
        <button class="nav-btn nav-btn--next" @click="createTextModel" :disabled="isLoading || !textPrompt.trim() || userCoins < 1">
          <span v-if="isLoading">Creating...</span>
          <span v-else-if="userCoins < 1">❌ No Coins</span>
          <span v-else><img src="/Images/Warhallcoin_128.png" style="height: 20px; vertical-align: middle; margin-right: 5px;" /> Create 3D Model</span>
        </button>
      </div>
    </div>

    <!-- Scan Miniature Screen -->
    <div v-if="currentScreen === 'scanInput'" class="scan-input-screen">
      <h1>Scan Miniature</h1>
      <p class="subtitle">Upload one or multiple photos. More angles = better results.</p>

      <div v-if="false" class="quality-preset-selector">
        <label>Quality Preset:</label>
        <div class="preset-options">
          <button :class="['preset-btn', { active: qualityPreset === 'standard' }]" @click="qualityPreset = 'standard'">
            ⚡ Standard
            <small>Fast &amp; Balanced</small>
          </button>
          <button :class="['preset-btn', { active: qualityPreset === 'high_quality' }]" @click="qualityPreset = 'high_quality'">
            ✨ High Quality
            <small>PBR Textures</small>
          </button>
        </div>
      </div>

      <div class="scan-slots">
        <div v-for="angle in ['front', 'left', 'back', 'right']" :key="angle" class="scan-slot" :class="{ required: angle === 'front' }">
          <div class="slot-label">
            <span>
              {{ angle === 'left' ? 'Left Arm' : angle === 'right' ? 'Right Arm' : angle === 'front' ? 'Front (required)' : angle.charAt(0).toUpperCase() + angle.slice(1) }}
            </span>
          </div>
          <div v-if="scanSlots[angle].preview" class="slot-preview">
            <img :src="scanSlots[angle].preview" :alt="angle" />
            <button class="slot-clear" @click="clearScanSlot(angle)">✕</button>
          </div>
          <div v-else class="slot-upload" @click="triggerScanUpload(angle)">
            <span>+</span>
            <input v-if="!chooseImages" :ref="`scanInput_${angle}`" type="file" accept="image/*" capture="environment" @change="handleScanUpload($event, angle)" style="display:none" />
            <input v-else :ref="`scanInput_${angle}`" type="file" accept="image/*" @change="handleScanUpload($event, angle)" style="display:none" />
          </div>
        </div>
      </div>

      <label class="scan-gallery-toggle">
        <input type="checkbox" v-model="chooseImages" />
        Choose images
      </label>

      <div class="screen-nav">
        <button class="nav-btn nav-btn--back" @click="goBack">Back</button>
        <a href="/store#Coins" target="_blank" class="nav-btn nav-btn--next" style="text-decoration:none;"><img src="/Images/Warhallcoin_128.png" style="height: 20px; vertical-align: middle; margin-right: 5px;" /> Buy</a>
        <button class="nav-btn nav-btn--next" @click="createScanModel" :disabled="isLoading || !scanSlots.front.file || userCoins < 1">
          <span v-if="isLoading">Creating...</span>
          <span v-else-if="userCoins < 1">❌ No Coins</span>
          <span v-else-if="!scanSlots.front.file">📷 Upload front image first</span>
          <span v-else><img src="/Images/Warhallcoin_128.png" style="height: 20px; vertical-align: middle; margin-right: 5px;" /> Create 3D Model</span>
        </button>
      </div>
    </div>

    <!-- 3D Viewer Screen -->
    <div v-if="currentScreen === 'viewer'" class="viewer-screen">
      <div class="viewer-header">
        <h1>Your creation</h1>
      </div>
      
      <ModelViewer 
        v-if="generatedModelUrl" 
        ref="modelViewer"
        :modelUrl="generatedModelUrl" 
        :modelData="modelMetadata"
      />

      <div class="screen-nav">
        <button class="nav-btn nav-btn--back" @click="goBack">Back</button>
        <button class="nav-btn nav-btn--next" @click="goToCategorize">Add</button>
      </div>
    </div>

    <!-- Categorize Screen -->
    <div v-if="currentScreen === 'categorize'" class="categorize-screen">
      <div class="categorize-container">

        <!-- Skin name — always visible at the top -->
        <div class="skin-name-field">
          <label class="skin-name-label">Skin Name</label>
          <input
            ref="skinNameInput"
            type="text"
            class="skin-name-input"
            v-model="standaloneSkinName"
            placeholder="Enter skin name..."
            maxlength="80"
          />
        </div>

        <!-- Catalogue-only toggle -->
        <label class="catalogue-only-label">
          <input type="checkbox" v-model="catalogueOnly" />
          I don't want to use this skin, only add to Catalogue
        </label>

        <WDSCategorySelector
          v-if="!catalogueOnly"
          ref="categorySelector"
          title="Use this as..."
          :image-url="pendingSnapshotUrl"
          subtitle="Select the category for your 3D model"
          :show-unit-name-input="false"
          @selection-changed="onCategorySelectionChanged"
        />

        <!-- Show snapshot preview when catalogue-only is checked -->
        <div v-if="catalogueOnly && pendingSnapshotUrl" class="catalogue-only-preview">
          <img :src="pendingSnapshotUrl" alt="Skin preview" />
        </div>
        <!-- Modifier Selection -->
        <div v-if="availableModifiers.length > 0" class="modifier-selection">
          <label class="modifier-label">Modifiers (Optional - Hold Ctrl/Cmd for multiple)</label>
          <select v-model="selectedModifiers" class="modifier-dropdown" multiple size="5">
            <option 
              v-for="modifier in availableModifiers" 
              :key="modifier.IdModifier" 
              :value="modifier.IdModifier"
            >
              {{ modifier.ModifierName }}
            </option>
          </select>
          <small class="modifier-hint">Hold Ctrl/Cmd to select multiple modifiers for this item variant</small>
        </div>
        
      </div>

      <div class="screen-nav">
        <button class="nav-btn nav-btn--back" @click="goBack">Back</button>
        <button
          class="nav-btn nav-btn--next"
          @click="goToTags"
          :disabled="!standaloneSkinName.trim() || (!catalogueOnly && (!categorySelection || !categorySelection.isComplete))"
        >Next</button>
      </div>

      <!-- Conflict dialog overlay -->
      <div v-if="conflictDialog" class="conflict-overlay">
        <div class="conflict-dialog">
          <p class="conflict-title">There is already a skin associated to <strong>{{ conflictDialog.unitName }}</strong>, do you want to override it, or add it?</p>
          <p class="conflict-hint">Multiple skins on the same unit will result in a random skin picked at creation time.</p>
          <div v-if="conflictDialog.skins && conflictDialog.skins.length" class="conflict-thumbs">
            <img
              v-for="s in conflictDialog.skins"
              :key="s.id"
              :src="s.thumbnailUrl"
              :title="s.name"
              class="conflict-thumb"
            />
          </div>
          <div class="conflict-actions">
            <button class="conflict-btn conflict-btn--cancel" @click="conflictDialog = null">Cancel</button>
            <button class="conflict-btn conflict-btn--override" @click="resolveConflict('override')">Override</button>
            <button class="conflict-btn conflict-btn--add" @click="resolveConflict('add')">Add</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Type / Visibility Screen -->
    <div v-if="currentScreen === 'permissions'" class="type-screen">
      <div class="type-container">
        <h2 class="type-title">Who can use this skin?</h2>

        <section class="type-section">
          <label class="type-radio-label">
            <input type="radio" v-model="skinVisibility" value="private" />
            <span><strong>Private</strong> — only me</span>
          </label>
          <label class="type-radio-label">
            <input type="radio" v-model="skinVisibility" value="public" />
            <span><strong>Public</strong> — other Warhall users can display this skin in Warhall</span>
          </label>
          <p v-if="skinVisibility === 'public'" class="type-hint type-hint--indent">Skins are only shown during gameplay and are not downloadable or reusable outside Warhall.</p>
        </section>

        <section v-if="skinVisibility === 'public'" class="type-section">
          <label class="type-check-label">
            <input type="checkbox" v-model="typeConfirmations.rights" />
            <span>I have the right to upload and share this content.</span>
          </label>
          <label class="type-check-label">
            <input type="checkbox" v-model="typeConfirmations.platformLicense" />
            <span>I allow Warhall to host and display this content within the platform.</span>
          </label>
          <label class="type-check-label">
            <input type="checkbox" v-model="typeConfirmations.playerUsage" />
            <span>I allow other players to use this content in Warhall games.</span>
          </label>
          <label class="type-check-label">
            <input type="checkbox" v-model="typeConfirmations.takedown" />
            <span>I understand that content may be removed if someone reports it as infringing their rights.</span>
          </label>
          <label class="type-check-label">
            <input type="checkbox" v-model="typeConfirmations.thirdParty" />
            <span>If this content includes material from other creators or brands, I understand it may be removed if requested.</span>
          </label>
        </section>

        <p class="type-notice">Warhall respects intellectual property rights and will remove content upon valid request.</p>
        <p class="type-notice">By uploading, you agree to the <a href="/terms" target="_blank" class="type-notice-link">Terms of Service</a>.</p>
      </div>

      <div class="screen-nav">
        <button class="nav-btn nav-btn--back" @click="goBack">Back</button>
        <button class="nav-btn nav-btn--next" @click="addModel" :disabled="!typeFormValid">Add Model</button>
      </div>
    </div>

    <!-- Tags Screen -->
    <div v-if="currentScreen === 'tags'" class="tags-screen">
      <div class="tags-container">
        <h2 class="tags-title">Tag your model</h2>
        <p class="tags-intro">Add at least <strong>3 tags</strong>. We suggest tags automatically, and you can add or remove any tag.</p>
        <div class="tags-input-row">
          <input
            v-model="manualTagInput"
            type="text"
            class="tags-input"
            maxlength="40"
            placeholder="Add a tag (for example: undead, cavalry, fire)"
            @keydown.enter.prevent="addManualTag"
          />
          <button class="tags-add-btn" type="button" @click="addManualTag" :disabled="!manualTagInput.trim()">Add</button>
        </div>
        <div v-if="tagsLoading" class="tags-loading">Generating suggestions...</div>
        <div v-if="tagError" class="tags-validation">{{ tagError }}</div>
        <template v-if="!tagsLoading">
          <div class="tags-validation" v-if="selectedTags.length > 0 && !tagsFormValid">
            Add at least {{ 3 - selectedTags.length }} more tag<span v-if="(3 - selectedTags.length) !== 1">s</span>.
          </div>
          <div class="tag-chips">
            <div v-for="tag in selectedTags" :key="tag" class="tag-chip">
              <span>{{ tag }}</span>
              <button type="button" class="tag-chip-remove" @click="removeTag(tag)" aria-label="Remove tag">x</button>
            </div>
          </div>
          <p class="tags-count">{{ selectedTags.length }} selected<span v-if="selectedTags.length < 3"> &mdash; {{ 3 - selectedTags.length }} more needed</span></p>
        </template>
      </div>

      <div class="screen-nav">
        <button class="nav-btn nav-btn--back" @click="goBack">Back</button>
        <button class="nav-btn nav-btn--next" @click="goToPermissions" :disabled="!tagsFormValid">Next</button>
      </div>
    </div>

    <!-- Upload Complete Screen -->
    <SkinSuccessScreen
      v-if="currentScreen === 'uploadComplete'"
      :image-url="uploadResult && uploadResult.imageUrl ? uploadResult.imageUrl : null"
      :skin-name="uploadResult && uploadResult.skinName ? uploadResult.skinName : null"
      title="Skin uploaded!"
      subtitle="Your skin is now part of your collection."
      action-label="Create more!"
      @action="createMore"
    />

    <!-- Loading Overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
      <p class="loading-message">{{ loadingMessage }}</p>
      <div class="progress-container" v-if="progress > 0">
        <div class="progress-bar" :style="{ width: progress + '%' }">
          <span class="progress-text">{{ Math.round(progress) }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ModelViewer from '@/components/ModelViewer.vue';
import WDSCategorySelector from '@/components/WDSCategorySelector.vue';
import SkinSuccessScreen from '@/components/SkinSuccessScreen.vue';

export default {
  name: 'ModelCreator',
  components: {
    ModelViewer,
    WDSCategorySelector,
    SkinSuccessScreen,
  },
  computed: {
    currentScreen() {
      return this.$route.query.screen || 'selection';
    },
    typeFormValid() {
      if (this.skinVisibility === 'private') return true;
      return this.typeConfirmations.rights &&
        this.typeConfirmations.platformLicense &&
        this.typeConfirmations.playerUsage &&
        this.typeConfirmations.takedown &&
        this.typeConfirmations.thirdParty;
    },
    tagsFormValid() {
      return this.selectedTags.length >= 3;
    },

    userCoins() {
      return this.$store.state.userCoins;
    },
  },

  data() {
    return {
      selectedMethod: null,
      capturedImage: null,
      capturedImageFile: null,
      cameraActive: false,
      mediaStream: null,
      isLoading: false,
      loadingMessage: '',
      progress: 0,
      generatedModelUrl: null,
      modelMetadata: {},
      pollInterval: null,
      isMobile: this.detectMobile(),
      existingTaskId: '',
      showTestInput: false,
      // Categorization
      categorySelection: null,
      catalogueOnly: false,
      standaloneSkinName: '',
      skinVisibility: 'private',
      conflictDialog: null,
      conflictResolution: null,
      typeConfirmations: {
        rights: false,
        platformLicense: false,
        playerUsage: false,
        takedown: false,
        thirdParty: false,
      },
      selectedTags: [],
      generatedTags: [],
      manualTagInput: '',
      tagsLoading: false,
      tagError: '',
      pendingGlbBlob: null,
      pendingSnapshotUrl: null,
      pendingPrepPromise: null,
      uploadResult: null,
      pendingTransforms: null,
      modelWrapped: false,
      availableModifiers: [],
      selectedModifiers: [],
      loadingModifiers: false,
      // GLB Upload
      uploadedGlbFile: null,
      glbBlobUrl: null,
      objTextureFile: null,
      objTextureBlobUrl: null,
      glbUrlDraft: '',
      glbUrlInput: '',
      showUrlInputScreen: false,
      glbTexUrlDraft: '',
      glbTexUrlInput: '',
      // Quality preset
      qualityPreset: 'high_quality', // 'standard' (no PBR) or 'high_quality' (PBR)
      // Wake lock
      _wakeLock: null,
      textPrompt: '',
      scanSlots: {
        front: { file: null, preview: null },
        left:  { file: null, preview: null },
        back:  { file: null, preview: null },
        right: { file: null, preview: null },
      },
      chooseImages: false,
      pendingTaskId: null,
    };
  },

  mounted() {
    this._onVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && this._wakeLock === null && this._wakeLockDesired) {
        await this.acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this.fetchUserCoins();
    // Auto-resume a pending generation if navigated here with a resumeTaskId query param
    if (this.$route.query.resumeTaskId) {
      this.existingTaskId = this.$route.query.resumeTaskId;
      this.$nextTick(() => this.loadExistingTask());
    }
  },

  methods: {
    async fetchUserCoins() {
      try {
        const response = await fetch('/GetUserCoins/', {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error(`Failed to fetch user coins: ${response.status} ${response.statusText}`);
        }
        const data = await response.json();
        if (data.error !== 0) {
          throw new Error(data.result || 'Failed to check coins');
        }
        const coins = data.result || 0;
        console.log('Coins fetched:', coins);
        this.$store.commit('setUserCoins', coins);
        console.log('Store updated with coins:', this.$store.state.userCoins);
      } catch (e) {
        console.error('Failed to fetch user coins:', e);
        // Do not override the store with 0 — logout.vue may have already set the correct value.
      }
    },

    normalizeTag(tag) {
      return String(tag || '').trim().toLowerCase().replace(/\s+/g, '-');
    },

    dedupeTags(tags) {
      const deduped = [];
      const seen = new Set();
      for (const raw of tags) {
        const normalized = this.normalizeTag(raw);
        if (!normalized || seen.has(normalized)) continue;
        seen.add(normalized);
        deduped.push(normalized);
      }
      return deduped;
    },

    addManualTag() {
      const normalized = this.normalizeTag(this.manualTagInput);
      if (!normalized) return;
      if (this.selectedTags.includes(normalized)) {
        this.manualTagInput = '';
        return;
      }
      this.selectedTags.push(normalized);
      this.manualTagInput = '';
      this.tagError = '';
    },

    removeTag(tag) {
      this.selectedTags = this.selectedTags.filter(t => t !== tag);
    },

    async fetchGeneratedTagsFromSnapshot() {
      if (!this.pendingSnapshotUrl) {
        this.generatedTags = [];
        this.selectedTags = [];
        this.tagError = 'No snapshot available, add your own tags manually.';
        return;
      }
      this.tagsLoading = true;
      this.tagError = '';
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 60000);
      try {
        const res = await fetch('/create-tags', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            image: this.pendingSnapshotUrl,
            name: `${(this.standaloneSkinName || 'skin').trim() || 'skin'}.png`,
          }),
        });
        if (!res.ok) throw new Error(`Tag generation failed: ${res.status}`);
        const data = await res.json();
        const generated = Array.isArray(data) ? this.dedupeTags(data) : [];
        this.generatedTags = generated;
        this.selectedTags = [...generated];
        if (this.selectedTags.length === 0) {
          this.tagError = 'No suggestions returned. Add tags manually.';
        }
      } catch (e) {
        this.generatedTags = [];
        this.selectedTags = [];
        this.tagError = e.name === 'AbortError'
          ? 'Tag generation timed out after 60 seconds. Add tags manually.'
          : 'Could not generate suggestions automatically. Add tags manually.';
        console.warn('Failed to generate tags:', e);
      } finally {
        clearTimeout(timeoutId);
        this.tagsLoading = false;
      }
    },

    async acquireWakeLock() {
      if (!('wakeLock' in navigator)) return;
      try {
        this._wakeLock = await navigator.wakeLock.request('screen');
        this._wakeLockDesired = true;
        this._wakeLock.addEventListener('release', () => { this._wakeLock = null; });
      } catch (e) {
        // Wake lock denied (e.g. low battery) — non-fatal, continue silently
      }
    },

    releaseWakeLock() {
      this._wakeLockDesired = false;
      if (this._wakeLock) {
        this._wakeLock.release();
        this._wakeLock = null;
      }
    },

    detectMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },

    navigateTo(screen) {
      window.scrollTo(0, 0);
      // Use replace for the selection screen (no back into a blank state)
      if (screen === 'selection') {
        this.$router.replace({ name: 'ModelCreator' });
      } else {
        this.$router.push({ name: 'ModelCreator', query: { screen } });
      }
    },

    navigateReplace(screen) {
      window.scrollTo(0, 0);
      // Replace instead of push — used after async generation so back doesn't re-trigger loading
      if (screen === 'selection') {
        this.$router.replace({ name: 'ModelCreator' });
      } else if (screen === 'viewer') {
        // Replace current entry directly with viewer — a two-step replace→push via 'selection'
        // would trigger beforeRouteUpdate with isGoingBack=true and call clearGlbFile(),
        // revoking any local blob URL before ModelViewer can load it.
        this.$router.replace({ name: 'ModelCreator', query: { screen: 'viewer' } });
      } else {
        this.$router.replace({ name: 'ModelCreator', query: { screen } });
      }
    },
    
    getQualityLabel() {
      return this.qualityPreset === 'standard' ? 'Standard' : 'High Quality (PBR)';
    },
    
    selectMethod(method) {
      this.selectedMethod = method;
      if (method === 'image') {
        this.navigateTo('imageInput');
      } else if (method === 'glb') {
        this.navigateTo('glbUpload');
      } else if (method === 'text') {
        this.navigateTo('textInput');
      } else if (method === 'scan') {
        this.navigateTo('scanInput');
      }
    },
    
    goBack() {
      this.$router.back();
    },
    
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.capturedImageFile = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.capturedImage = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
    
    async openCamera() {
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'environment' } 
        });
        this.cameraActive = true;
        this.$nextTick(() => {
          this.$refs.videoElement.srcObject = this.mediaStream;
        });
      } catch (error) {
        console.error('Camera access error:', error);
        alert('Could not access camera. Please check permissions.');
      }
    },
    
    capturePhoto() {
      const video = this.$refs.videoElement;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        this.capturedImageFile = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
        this.capturedImage = canvas.toDataURL('image/jpeg');
        this.closeCamera();
      }, 'image/jpeg', 0.9);
    },
    
    closeCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(track => track.stop());
        this.mediaStream = null;
      }
      this.cameraActive = false;
    },
    
    clearImage() {
      this.capturedImage = null;
      this.capturedImageFile = null;
    },
    
    // GLB Upload Methods
    triggerGlbUpload() {
      this.$refs.glbFileInput.click();
    },
    
    handleGlbUpload(event) {
      const file = event.target.files[0];
      if (file) {
        // Validate file type
        const nameLower = file.name.toLowerCase();
        if (!nameLower.endsWith('.glb') && !nameLower.endsWith('.fbx') && !nameLower.endsWith('.obj')) {
          alert('Please select a valid GLB, FBX or OBJ file.');
          return;
        }
        
        // Validate file size (max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
          alert('File is too large. Maximum size is 10MB.');
          return;
        }
        
        this.uploadedGlbFile = file;
        this.loadGlbModel();
      }
    },
    
    loadGlbModel() {
      if (!this.uploadedGlbFile) return;
      
      // Create a blob URL for the model file
      this.glbBlobUrl = URL.createObjectURL(this.uploadedGlbFile);
      this.generatedModelUrl = this.glbBlobUrl;

      // Create blob URL for texture if provided (OBJ only)
      if (this.objTextureBlobUrl) {
        URL.revokeObjectURL(this.objTextureBlobUrl);
        this.objTextureBlobUrl = null;
      }
      if (this.objTextureFile) {
        this.objTextureBlobUrl = URL.createObjectURL(this.objTextureFile);
      }
      
      // Set model metadata
      this.modelMetadata = {
        fileName: this.uploadedGlbFile.name,
        fileSize: this.formatFileSize(this.uploadedGlbFile.size),
        source: 'Upload',
        textureUrl: this.objTextureBlobUrl || null,
      };
      
      // Go to viewer
      this.navigateReplace('viewer');
    },

    handleObjTextureUpload(event) {
      const file = event.target.files[0];
      if (file) this.objTextureFile = file;
    },

    clearObjTexture() {
      this.objTextureFile = null;
      if (this.$refs.objTextureInput) this.$refs.objTextureInput.value = '';
    },
    
    clearGlbFile() {
      if (this.glbBlobUrl) {
        URL.revokeObjectURL(this.glbBlobUrl);
        this.glbBlobUrl = null;
      }
      if (this.objTextureBlobUrl) {
        URL.revokeObjectURL(this.objTextureBlobUrl);
        this.objTextureBlobUrl = null;
      }
      this.uploadedGlbFile = null;
      this.objTextureFile = null;
    },

    confirmGlbUrl() {
      const url = this.glbUrlDraft.trim();
      if (!url) return;
      this.glbUrlInput = url;
      this.glbTexUrlInput = this.glbTexUrlDraft.trim();
      this.showUrlInputScreen = false;
      this.loadUrlModel();
    },

    clearGlbUrl() {
      this.glbUrlInput = '';
      this.glbUrlDraft = '';
      this.glbTexUrlInput = '';
      this.glbTexUrlDraft = '';
      this.showUrlInputScreen = false;
    },

    loadUrlModel() {
      const url = this.glbUrlInput.trim();
      if (!url) return;

      // Derive file name from URL path for format detection
      const pathPart = url.split('?')[0];
      const fileName = pathPart.substring(pathPart.lastIndexOf('/') + 1) || 'model.glb';

      this.generatedModelUrl = url;
      this.modelMetadata = {
        fileName,
        fileSize: 'Remote',
        source: 'Link',
        textureUrl: this.glbTexUrlInput || null,
      };
      this.navigateReplace('viewer');
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    
    async loadExistingTask() {
      try {
        this.isLoading = true;
        this.loadingMessage = 'Checking task status...';
        this.progress = 10;
        
        const taskId = this.existingTaskId.trim();
        
        // Step 1: Check task status and type
        const response = await fetch(`${this.$config.serverUrl}/api/tripo/task/${taskId}`, {
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.error !== 0) {
          throw new Error(data.result || 'Failed to fetch task status');
        }
        
        const taskData = data.result;
        
        // Check if task is complete
        if (taskData.status !== 'success') {
          throw new Error(`Task is not complete. Status: ${taskData.status}`);
        }
        
        this.progress = 50;
        
        // Always use server proxy URL to avoid CORS issues with direct Tripo CDN
        this.generatedModelUrl = `${this.$config.serverUrl}/api/tripo/download-model/${taskId}`;
        this.modelMetadata = {
          generationTaskId: taskId,
          renderedImageUrl: taskData.output?.rendered_image || null,
          faceCount: 15000,
          quality: this.getQualityLabel(),
          modelUrl: this.generatedModelUrl,
          textureUrl: null,
          source: 'Created'
        };
        this.pendingTaskId = taskId;
        this.isLoading = false;
        this.navigateReplace('viewer');
        
      } catch (error) {
        this.isLoading = false;
        console.error('Load existing task error:', error);
        
        let errorMsg = 'Failed to load existing model.';
        if (error.message.includes('2006')) {
          errorMsg = 'Tripo API Error: This task type cannot be converted.\n\nPossible reasons:\n1. Task is already a conversion (use that task ID directly)\n2. Task is not a model generation task\n3. Try using a different task ID from a completed model generation';
        } else if (error.message.includes('403')) {
          errorMsg = 'Tripo API Error (403 Forbidden): Invalid API key or insufficient credits.';
        } else if (error.message.includes('Authentication failed')) {
          errorMsg = 'Authentication Error: Please log in at the home page first, then try again.';
        } else if (error.message.includes('not complete')) {
          errorMsg = error.message + '\n\nPlease wait for the task to complete before loading it.';
        } else {
          errorMsg = `Error: ${error.message}`;
        }
        
        alert(errorMsg);
      }
    },
    
    async createModel() {
      try {
        this.isLoading = true;

        const coinsresponse = await fetch('/GetUserCoins/', {
          credentials: 'include'
        });

        if (!coinsresponse.ok) {
          throw new Error(`Failed to fetch user coins: ${coinsresponse.status} ${coinsresponse.statusText}`);
        }

        const coinsData = await coinsresponse.json();
        
        if (coinsData.error !== 0) {
          throw new Error(coinsData.result || 'Failed to check coins');
        }

        const totalCoins = coinsData.result;
        
        if (totalCoins < 1) {
          throw new Error('Insufficient coins. You need at least 1 coin to create a model.');
        }

        this.loadingMessage = 'Uploading image...';
        this.progress = 0;
        
        // Step 1: Upload image
        const imageToken = await this.uploadImage();
        
        // Step 2: Generate model
        this.loadingMessage = 'Generating 3D model...';
        this.progress = 10;
        const taskId = await this.generateModel(imageToken);
        
        // Step 3: Poll for completion and get GLB output
        const generationResult = await this.pollTaskStatus(taskId);
        
        // Step 4: Set download URL and metadata - always use server proxy to avoid CORS
        this.generatedModelUrl = `${this.$config.serverUrl}/api/tripo/download-model/${taskId}`;
        this.modelMetadata = {
          generationTaskId: taskId,
          renderedImageUrl: generationResult?.output?.rendered_image || null,
          faceCount: 15000,
          quality: this.getQualityLabel(),
          modelUrl: this.generatedModelUrl,
          textureUrl: null,
          source: 'Created'
        };
        this.pendingTaskId = taskId;
        this.isLoading = false;
        await this.fetchUserCoins();
        // Small delay to ensure store update propagates
        await new Promise(resolve => setTimeout(resolve, 100));
        this.navigateReplace('viewer');
        
      } catch (error) {
        this.isLoading = false;
        console.error('Model creation error:', error);
        
        // Show detailed error message
        let errorMsg = 'Failed to create model.';
        if (error.message.includes('403')) {
          errorMsg = 'Tripo API Error (403 Forbidden):\n\nPossible causes:\n1. Invalid or expired TRIPO_API_KEY in server .env file\n2. Insufficient Tripo credits on your account\n3. API key lacks required permissions\n\nPlease check:\n- Server logs for detailed Tripo error\n- Tripo dashboard: platform.tripo3d.ai\n- TRIPO_API_KEY environment variable';
        } else if (error.message.includes('CORS')) {
          errorMsg = 'CORS Error: The server is not configured to accept requests from this origin. Please check server CORS settings.';
        } else if (error.message.includes('credentials')) {
          errorMsg = 'Authentication Error: Cannot send credentials with current CORS configuration. Server must allow specific origin, not wildcard (*).';
        } else if (error.message.includes('Failed to fetch')) {
          errorMsg = 'Network Error: Cannot connect to server. Possible causes:\n1. CORS policy blocking the request\n2. Server is down\n3. Not logged in (missing session cookie)\n\nTry logging in at the home page first.';
        } else if (error.message.includes('Authentication failed')) {
          errorMsg = 'Authentication Error: Please log in at the home page first, then try again.';
        } else {
          errorMsg = `Error: ${error.message}`;
        }
        
        alert(errorMsg);
      }
    },
    
    async uploadImage() {
      const compressed = await this.compressImage(this.capturedImageFile, { maxDimension: 1920, quality: 0.85 });
      const formData = new FormData();
      formData.append('file', compressed);
      
      let response;
      try {
        response = await fetch(`${this.$config.serverUrl}/api/tripo/upload-image`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
      } catch (err) {
        throw new Error(`Network error during upload: ${err.message}. Check CORS settings or login status.`);
      }
      
      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}: ${response.statusText}`);
      }
      
      let data;
      try {
        data = await response.json();
      } catch (err) {
        throw new Error('Server returned invalid response. CORS or authentication issue.');
      }
      
      if (data.error !== 0) {
        if (data.error === 1) {
          throw new Error('Authentication failed. Please log in first.');
        }
        throw new Error(data.result || 'Upload failed');
      }
      
      return data.result.image_token;
    },

    compressImage(file, { maxDimension = 1920, quality = 0.85 } = {}) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          let { width, height } = img;
          if (width > maxDimension || height > maxDimension) {
            if (width >= height) {
              height = Math.round((height / width) * maxDimension);
              width = maxDimension;
            } else {
              width = Math.round((width / height) * maxDimension);
              height = maxDimension;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            blob => {
              if (!blob) { reject(new Error('Image compression failed')); return; }
              resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Failed to load image')); };
        img.src = objectUrl;
      });
    },

    async uploadSingleImageFile(file) {
      const compressed = await this.compressImage(file);
      const formData = new FormData();
      formData.append('file', compressed);
      const response = await fetch(`${this.$config.serverUrl}/api/tripo/upload-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`Image upload failed (HTTP ${response.status}). The file may be too large or you may not be authenticated.`);
      }
      const data = await response.json();
      if (data.error !== 0) throw new Error(data.result || 'Upload failed');
      return data.result.image_token;
    },

    async createTextModel() {
      try {
        this.isLoading = true;

        const coinsresponse = await fetch('/GetUserCoins/', {
          credentials: 'include'
        });

        if (!coinsresponse.ok) {
          throw new Error(`Failed to fetch user coins: ${coinsresponse.status} ${coinsresponse.statusText}`);
        }

        const coinsData = await coinsresponse.json();
        
        if (coinsData.error !== 0) {
          throw new Error(coinsData.result || 'Failed to check coins');
        }

        const totalCoins = coinsData.result;
        
        if (totalCoins < 1) {
          throw new Error('Insufficient coins. You need at least 1 coin to create a model.');
        }

        this.progress = 5;
        this.loadingMessage = 'Submitting prompt...';

        const response = await fetch(`${this.$config.serverUrl}/api/tripo/generate-text-model`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ prompt: this.textPrompt, quality_preset: this.qualityPreset }),
        });
        const data = await response.json();
        if (data.error !== 0) throw new Error(data.result || 'Generation failed');
        const taskId = data.result.task_id;

        this.loadingMessage = 'Generating 3D model...';
        const textResult = await this.pollTaskStatus(taskId);

        this.generatedModelUrl = `${this.$config.serverUrl}/api/tripo/download-model/${taskId}`;
        this.modelMetadata = {
          generationTaskId: taskId,
          renderedImageUrl: textResult?.output?.rendered_image || null,
          faceCount: 15000,
          quality: this.getQualityLabel(),
          modelUrl: this.generatedModelUrl,
          textureUrl: null,
          source: 'Created',
        };
        this.pendingTaskId = taskId;
        this.isLoading = false;
        await this.fetchUserCoins();
        // Small delay to ensure store update propagates
        await new Promise(resolve => setTimeout(resolve, 100));
        this.navigateReplace('viewer');
      } catch (error) {
        this.isLoading = false;
        console.error('Model creation error:', error);
        alert(`Failed to create model: ${error.message}`);
      }
    },

    async createScanModel() {
      try {
        this.isLoading = true;
        this.progress = 0;
        const angles = ['front', 'left', 'back', 'right'];
        const filledAngles = angles.filter(a => this.scanSlots[a].file);

        if (filledAngles.length === 1) {
          // Single image: delegate to the standard From Image pipeline
          this.capturedImageFile = this.scanSlots.front.file;
          this.isLoading = false;
          return this.createModel();
        }

        const coinsresponse = await fetch('/GetUserCoins/', {
          credentials: 'include'
        });

        if (!coinsresponse.ok) {
          throw new Error(`Failed to fetch user coins: ${coinsresponse.status} ${coinsresponse.statusText}`);
        }

        const coinsData = await coinsresponse.json();
        
        if (coinsData.error !== 0) {
          throw new Error(coinsData.result || 'Failed to check coins');
        }

        const totalCoins = coinsData.result;
        
        if (totalCoins < 1) {
          throw new Error('Insufficient coins. You need at least 1 coin to create a model.');
        }

        this.loadingMessage = `Uploading images...`;
        const fileTokens = {};
        for (const angle of filledAngles) {
          fileTokens[angle] = await this.uploadSingleImageFile(this.scanSlots[angle].file);
        }
        this.progress = 10;

        let taskId;
        {
          // 2–4 images: multiview_to_model directly
          this.loadingMessage = 'Building 3D model...';
          const files = angles.map(a => fileTokens[a] ? { type: 'jpg', file_token: fileTokens[a] } : {});
          const modelRes = await fetch(`${this.$config.serverUrl}/api/tripo/generate-multiview-model`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ files, quality_preset: this.qualityPreset }),
          });
          if (!modelRes.ok) throw new Error(`Model generation failed (HTTP ${modelRes.status})`);
          const modelData = await modelRes.json();
          if (modelData.error !== 0) throw new Error(modelData.result || 'Model generation failed');
          taskId = modelData.result.task_id;
          const scanResult = await this.pollTaskStatusRange(taskId, 10, 100);

          this.generatedModelUrl = `${this.$config.serverUrl}/api/tripo/download-model/${taskId}`;
          this.modelMetadata = {
            generationTaskId: taskId,
            renderedImageUrl: scanResult?.output?.rendered_image || null,
            faceCount: 15000,
            quality: this.getQualityLabel(),
            modelUrl: this.generatedModelUrl,
            textureUrl: null,
            source: 'Created',
          };
          this.pendingTaskId = taskId;
        }
        this.isLoading = false;
        await this.fetchUserCoins();
        // Small delay to ensure store update propagates
        await new Promise(resolve => setTimeout(resolve, 100));
        this.navigateReplace('viewer');
      } catch (error) {
        this.isLoading = false;
        console.error('Scan model creation error:', error);
        alert(`Failed to create model: ${error.message}`);
      }
    },

    async pollTaskStatusRange(taskId, progressMin, progressMax) {
      return new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const response = await fetch(`${this.$config.serverUrl}/api/tripo/task/${taskId}`, {
              credentials: 'include',
            });
            const data = await response.json();
            if (data.error !== 0) { clearInterval(interval); reject(new Error(data.result || 'Task failed')); return; }
            const status = data.result.status;
            const progress = data.result.progress || 0;
            this.progress = Math.min(progressMin + (progress / 100) * (progressMax - progressMin), progressMax);
            if (status === 'success') { clearInterval(interval); resolve(data.result); }
            else if (status === 'failed') { clearInterval(interval); reject(new Error('Generation failed')); }
            else if (status === 'banned') { clearInterval(interval); reject(new Error('Image violates content policy')); }
          } catch (error) { clearInterval(interval); reject(error); }
        }, 3000);
      });
    },

    handleScanUpload(event, angle) {
      const file = event.target.files[0];
      if (!file) return;
      this.scanSlots[angle].file = file;
      this.scanSlots[angle].preview = URL.createObjectURL(file);
    },

    triggerScanUpload(angle) {
      const ref = this.$refs[`scanInput_${angle}`];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el) el.click();
    },

    clearScanSlot(angle) {
      this.scanSlots[angle].file = null;
      this.scanSlots[angle].preview = null;
    },

    clearScanSlots() {
      ['front', 'left', 'back', 'right'].forEach(a => {
        this.scanSlots[a].file = null;
        this.scanSlots[a].preview = null;
      });
    },

    async generateModel(imageToken) {
      const response = await fetch(`${this.$config.serverUrl}/api/tripo/generate-model`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_token: imageToken,
          options: {
            face_limit: 15000,
            quality_preset: this.qualityPreset
          }
        })
      });
      
      const data = await response.json();
      
      if (data.error !== 0) {
        throw new Error(data.result || 'Generation failed');
      }
      
      return data.result.task_id;
    },
    
    async pollTaskStatus(taskId) {
      return new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          try {
            const response = await fetch(`${this.$config.serverUrl}/api/tripo/task/${taskId}`, {
              credentials: 'include'
            });
            
            const data = await response.json();
            
            if (data.error !== 0) {
              clearInterval(interval);
              reject(new Error(data.result || 'Task failed'));
              return;
            }
            
            const status = data.result.status;
            const progress = data.result.progress || 0;
            
            // Update progress (10% to 100%)
            this.progress = Math.min(10 + (progress * 0.9), 100);
            
            if (status === 'success') {
              clearInterval(interval);
              resolve(data.result);
            } else if (status === 'failed') {
              clearInterval(interval);
              reject(new Error('Model generation failed'));
            } else if (status === 'banned') {
              clearInterval(interval);
              reject(new Error('Image violates content policy'));
            }
          } catch (error) {
            clearInterval(interval);
            reject(error);
          }
        }, 3000); // Poll every 3 seconds
      });
    },
    
    async downloadModel() {
      if (this.modelMetadata.source === 'Upload' && this.uploadedGlbFile) {
        // For uploaded files, download the original file
        const link = document.createElement('a');
        link.href = this.glbBlobUrl;
        link.download = this.uploadedGlbFile.name;
        link.click();
      } else {
        try {
          const glbBlob = await this.$refs.modelViewer.exportGLB();
          const url = URL.createObjectURL(glbBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `model_${this.modelMetadata.generationTaskId || Date.now()}.glb`;
          link.click();
          URL.revokeObjectURL(url);
        } catch (err) {
          console.error('Export failed:', err);
          alert('Failed to export model.');
        }
      }
    },
    
    createAnother() {
      this.clearImage();
      this.clearGlbFile();
      this.clearGlbUrl();
      this.clearScanSlots();
      this.textPrompt = '';
      this.generatedModelUrl = null;
      this.modelMetadata = {};
      this.modelWrapped = false;
      this.progress = 0;
      this.availableModifiers = [];
      this.selectedModifiers = [];
      this.navigateTo('selection');
    },
    
    copyGenerationTaskId() {
      navigator.clipboard.writeText(this.modelMetadata.generationTaskId);
      alert('Task ID copied! Use this for "Load Existing Model"');
    },
    
    copyUrl() {
      navigator.clipboard.writeText(this.generatedModelUrl);
      alert('Download URL copied to clipboard!');
    },

    // Categorization methods
    async goToCategorize() {
      this.acquireWakeLock();
      if (this.$refs.modelViewer) {
        this.$refs.modelViewer.closeAllPanels();
        const viewer = this.$refs.modelViewer;
        const isLink = this.modelMetadata.source === 'Link';
        // Await snapshot so it's available for display before the categorize screen opens
        this.pendingSnapshotUrl = await viewer.captureSnapshot(512, 512);
        if (isLink) {
          // For Link: capture transforms (accumulated by closeAllPanels) — no GLB export
          this.pendingTransforms = viewer.getModelTransforms();
          this.pendingPrepPromise = Promise.resolve();
        } else if (this.modelMetadata.source === 'Upload') {
          // For Upload: bake all edits (including user scale) into the GLB, same as Created.
          // SkinLayerSize is stored as 1.0 so the game engine applies no additional scaling.
          this.pendingTransforms = null;
          this.pendingPrepPromise = viewer.exportGLB().then(blob => { this.pendingGlbBlob = blob; });
        } else {
          // For Created (Tripo AI): export with user scale baked into the GLB
          this.pendingTransforms = null;
          this.pendingPrepPromise = viewer.exportGLB().then(blob => { this.pendingGlbBlob = blob; });
        }
      } else {
        this.pendingPrepPromise = null;
      }
      this.navigateTo('categorize');
      this.categorySelection = null;
      this.standaloneSkinName = '';
      this.$nextTick(() => {
        if (this.$refs.skinNameInput) {
          this.$refs.skinNameInput.focus();
        }
      });
    },

    onCategorySelectionChanged(selectionData) {
      this.categorySelection = selectionData;
    },

    goToPermissions() {
      this.navigateTo('permissions');
    },

    async goToTags() {
      // Check for costume conflict before proceeding
      if (!this.catalogueOnly && this.categorySelection && this.categorySelection.isComplete) {
        // Extract the profile/terrain/token ID for conflict check
        const skinType = this.categorySelection.skinType || 'model';
        let profileId;
        if (skinType === 'terrain') {
          profileId = this.categorySelection.selectedTerrain && this.categorySelection.selectedTerrain.Id;
        } else if (skinType === 'token') {
          profileId = this.categorySelection.selectedToken && this.categorySelection.selectedToken.Id;
        } else {
          profileId = this.categorySelection.selectedUnitId;
        }

        if (profileId) {
          try {
            const res = await fetch('/CheckCostumeConflict', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                profileId: profileId,
                selectedOptions: this.categorySelection.selectedOptions || [],
                gameId: this.categorySelection.game && this.categorySelection.game.IdGame,
              }),
            });
            const data = await res.json();
            if (data.error === 0 && data.hasConflict) {
              const unitName = skinType === 'terrain' 
                ? this.categorySelection.selectedTerrain.Name
                : skinType === 'token'
                ? this.categorySelection.selectedToken.Name
                : this.categorySelection.selectedUnitName;
              this.conflictDialog = {
                unitName: unitName,
                skins: data.existingSkins || [],
              };
              return;
            }
          } catch (e) {
            // Non-blocking — proceed even if the check fails
          }
        }
      }
      this.isLoading = true;
      this.loadingMessage = 'Generating tags from image...';
      try {
        await this.fetchGeneratedTagsFromSnapshot();
      } finally {
        this.isLoading = false;
      }
      this.navigateTo('tags');
    },

    resolveConflict(mode) {
      this.conflictResolution = mode;
      this.conflictDialog = null;
      this.navigateTo('tags');
    },

    createMore() {
      this.uploadResult = null;
      this.pendingSnapshotUrl = null;
      this.pendingPrepPromise = null;
      this.pendingTransforms = null;
      this.clearImage();
      this.clearGlbFile();
      this.clearScanSlots();
      this.generatedModelUrl = null;
      this.pendingGlbBlob = null;
      this.modelMetadata = {};
      this.progress = 0;
      this.categorySelection = null;
      this.catalogueOnly = false;
      this.selectedTags = [];
      this.generatedTags = [];
      this.manualTagInput = '';
      this.tagError = '';
      this.skinVisibility = 'private';
      this.conflictDialog = null;
      this.conflictResolution = null;
      this.typeConfirmations = { rights: false, platformLicense: false, playerUsage: false, takedown: false, thirdParty: false };
      this.pendingTaskId = null;
      this.navigateReplace('selection');
    },

    backToViewer() {
      this.$router.back();
    },

    async addModel() {
      if (!this.catalogueOnly && (!this.categorySelection || !this.categorySelection.isComplete)) {
        alert('Please select a category (unit, terrain, or token).');
        return;
      }

      try {
        this.isLoading = true;
        await this.acquireWakeLock();
        this.loadingMessage = 'Preparing model...';

        // Wait for background GLB export started in goToCategorize
        if (this.pendingPrepPromise) {
          try {
            await this.pendingPrepPromise;
          } catch (e) {
            throw new Error(`Failed to prepare model: ${e.message}`);
          } finally {
            this.pendingPrepPromise = null;
          }
        }

        const skinName = this.standaloneSkinName.trim() || `Skin_${Date.now()}`;

        if (this.modelMetadata.source === 'Upload') {
          // Upload the viewer-modified GLB (exported by goToCategorize with all edits baked in)
          if (!this.pendingGlbBlob) throw new Error('Exported model data is missing. Please go back and try again.');
          this.loadingMessage = 'Uploading 3D file to server...';
          const modelFormData = new FormData();
          modelFormData.append('skin_name', skinName);
          modelFormData.append('file', this.pendingGlbBlob, `${skinName}.glb`);
          const uploadResponse = await fetch(`${this.$config.serverUrl}/api/tripo/upload-user-glb`, {
            method: 'POST',
            credentials: 'include',
            body: modelFormData
          });
          const uploadData = await uploadResponse.json();
          if (uploadData.error !== 0) throw new Error(uploadData.result || 'Failed to upload 3D file');

          // If OBJ + local texture, upload texture too
          let textureS3Url = null;
          if (this.objTextureFile) {
            this.loadingMessage = 'Uploading texture...';
            const texFormData = new FormData();
            texFormData.append('skin_name', skinName + '_texture');
            texFormData.append('file', this.objTextureFile);
            const texResponse = await fetch(`${this.$config.serverUrl}/api/tripo/upload-user-glb`, {
              method: 'POST',
              credentials: 'include',
              body: texFormData
            });
            const texData = await texResponse.json();
            if (texData.error !== 0) throw new Error(texData.result || 'Failed to upload texture');
            textureS3Url = texData.result.s3_url;
          }

          this.loadingMessage = 'Adding model to your collection...';
          const payload = {
            model_url: uploadData.result.s3_url,
            texture_url: textureS3Url,
            skin_name: skinName,
            model_size: 1.0,
            transforms: null,
            is_public: this.skinVisibility === 'public' ? 1 : 0,
            thumbnail_data_url: this.pendingSnapshotUrl || null
            // No task_id — Upload flow deducts coin inside create-and-link-skin
          };
          const response = await fetch(`${this.$config.serverUrl}/api/tripo/create-and-link-skin`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const data = await response.json();
          if (data.error !== 0) throw new Error(data.result || 'Failed to add model');

          this.uploadResult = {
            skinName,
            skinId: data.result.skin_id,
            imageUrl: data.result.thumbnail_url || this.pendingSnapshotUrl || null,
          };

        } else if (this.modelMetadata.source === 'Link') {
          // Do NOT upload the model — use the original URLs as-is
          // Only the thumbnail snapshot is sent to our server (embedded in the payload)
          this.loadingMessage = 'Adding model to your collection...';
          const payload = {
            model_url: this.generatedModelUrl,
            texture_url: this.modelMetadata.textureUrl || null,
            skin_name: skinName,
            model_size: this.pendingTransforms ? this.pendingTransforms.scale : 1.0,
            transforms: this.pendingTransforms || null,
            is_public: this.skinVisibility === 'public' ? 1 : 0,
            thumbnail_data_url: this.pendingSnapshotUrl || null
            // No task_id — Link flow deducts coin inside create-and-link-skin
          };
          const response = await fetch(`${this.$config.serverUrl}/api/tripo/create-and-link-skin`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
          const data = await response.json();
          if (data.error !== 0) throw new Error(data.result || 'Failed to add model');

          this.uploadResult = {
            skinName,
            skinId: data.result.skin_id,
            imageUrl: data.result.thumbnail_url || this.pendingSnapshotUrl || null,
          };

        } else {
          // Use GLB blob pre-exported in goToCategorize
          if (!this.pendingGlbBlob) throw new Error('Prepared model data is missing. Please go back and try again.');

          this.loadingMessage = 'Uploading model to server...';
          const formData = new FormData();
          formData.append('glb', this.pendingGlbBlob, `${skinName}_${Date.now()}.glb`);
          formData.append('skin_name', skinName);
          formData.append('model_size', '1.0');
          formData.append('is_public', this.skinVisibility === 'public' ? '1' : '0');
          if (this.pendingSnapshotUrl) formData.append('thumbnail_data_url', this.pendingSnapshotUrl);
          // task_id tells the server to clear PendingGeneration (coin was already deducted at generation)
          if (this.pendingTaskId) formData.append('task_id', this.pendingTaskId);

          const response = await fetch(`${this.$config.serverUrl}/api/tripo/create-and-link-skin`, {
            method: 'POST',
            credentials: 'include',
            body: formData
          });
          const data = await response.json();
          if (data.error !== 0) throw new Error(data.result || 'Failed to add model');

          this.uploadResult = {
            skinName,
            skinId: data.result.skin_id,
            imageUrl: data.result.thumbnail_url || this.pendingSnapshotUrl || null,
          };
        }

        // Clear working state
        this.isLoading = false;
        if (this.$refs.categorySelector) this.$refs.categorySelector.clearCache();
        this.clearImage();
        this.clearGlbFile();
        this.generatedModelUrl = null;
        this.pendingGlbBlob = null;
        this.pendingSnapshotUrl = null;
        this.modelMetadata = {};
        this.progress = 0;

        // Assign skin to the selected profile/terrain/token if a category was chosen
        if (!this.catalogueOnly && this.categorySelection && this.categorySelection.isComplete) {
          // Extract the profile/terrain/token ID for the assignment
          const skinType = this.categorySelection.skinType || 'model';
          let profileId;
          if (skinType === 'terrain') {
            profileId = this.categorySelection.selectedTerrain && this.categorySelection.selectedTerrain.Id;
          } else if (skinType === 'token') {
            profileId = this.categorySelection.selectedToken && this.categorySelection.selectedToken.Id;
          } else {
            profileId = this.categorySelection.selectedUnitId;
          }

          if (profileId) {
            this.loadingMessage = 'Assigning skin...';
            const selectedOptions = this.categorySelection.selectedOptions || [];
            const modifiers = selectedOptions.length > 0 ? selectedOptions : null;
            const assignBody = {
              skinId: this.uploadResult.skinId,
              profileId: profileId,
              selectedOptions: modifiers,
              gameId: this.categorySelection.game && this.categorySelection.game.IdGame,
            };
            if (this.conflictResolution) assignBody.mode = this.conflictResolution;
            console.log('[AssignCatalogueSkin] profileId:', profileId, '| idSkin:', this.uploadResult.skinId, '| modifiers:', modifiers);
            const assignRes = await fetch('/AssignCatalogueSkin', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(assignBody),
            });
            const assignData = await assignRes.json();
            if (assignData.error !== 0 && assignData.error !== 6) {
              throw new Error(`Skin assignment failed (code ${assignData.error}): ${assignData.result}`);
            }

            if (this.selectedTags.length > 0) {
              try {
                const itemResponse = await fetch(`/GetItemId/${encodeURIComponent(profileId)}/${this.categorySelection.game.IdGame}`, {
                  credentials: 'include',
                });
                const itemData = await itemResponse.json();
                if (itemData && itemData.IdItem) {
                  await fetch('/create-item-tags', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ itemId: itemData.IdItem, tags: this.selectedTags }),
                  });
                } else {
                  console.warn('Skipping create-item-tags: item id could not be resolved');
                }
              } catch (tagErr) {
                console.warn('Tags could not be persisted on item:', tagErr);
              }
            }
          }
        }
        this.conflictResolution = null;
        this.selectedTags = [];
        this.generatedTags = [];
        this.manualTagInput = '';
        this.tagError = '';

        this.releaseWakeLock();
        this.navigateReplace('uploadComplete');

      } catch (error) {
        this.isLoading = false;
        this.releaseWakeLock();
        console.error('Add model error:', error);
        alert(`Failed to add model: ${error.message}`);
      }
    },

    async saveModel() {
      if (!this.categorySelection || !this.categorySelection.isComplete) {
        alert('Please complete all category selections.');
        return;
      }

      // TODO: Implement saving the model to the hierarchy
      // This will involve:
      // 1. Creating/updating the unit in units.json
      // 2. Adding the unit ID to the hierarchy at the selected path
      // 3. Saving the GLB file to the appropriate location
      
      const fullPath = [...this.categorySelection.sectionPath, this.categorySelection.unitName];
      console.log('Saving model to:', {
        game: this.categorySelection.game.GameName,
        gameId: this.categorySelection.game.IdGame,
        sections: this.categorySelection.sections,
        sectionPath: this.categorySelection.sectionPath,
        unitName: this.categorySelection.unitName,
        modelUrl: this.generatedModelUrl
      });
      
      alert('Model categorization saved!\n\nGame: ' + this.categorySelection.game.GameName + '\nPath: ' + fullPath.join(' > '));
      
      // Clear the category selector cache after successful save
      if (this.$refs.categorySelector) {
        this.$refs.categorySelector.clearCache();
      }
      
      // Return to selection screen
      this.clearImage();
      this.generatedModelUrl = null;
      this.modelMetadata = {};
      this.progress = 0;
      this.categorySelection = null;
      this.navigateTo('selection');
    }
  },

  beforeRouteUpdate(to, from, next) {
    // Browser back button navigated within ModelCreator
    const newScreen = to.query.screen || 'selection';
    const oldScreen = from.query.screen || 'selection';
    const isGoingBack = !to.query.screen || history.state?.position < (from.state?.position ?? Infinity);

    if (isGoingBack) {
      // Clean up screen-specific state when leaving a screen via back
      if (oldScreen === 'imageInput' || oldScreen === 'glbUpload' ||
          oldScreen === 'textInput' || oldScreen === 'scanInput') {
        if (this.cameraActive) this.closeCamera();
        this.clearImage();
        this.clearGlbFile();
        this.textPrompt = '';
        this.clearScanSlots();
      }
      if (oldScreen === 'categorize') {
        this.categorySelection = null;
        this.catalogueOnly = false;
        this.standaloneSkinName = '';
        if (this.$refs.categorySelector) this.$refs.categorySelector.reset();
      }
      if (oldScreen === 'permissions') {
        this.skinVisibility = 'private';
        this.typeConfirmations = { rights: false, platformLicense: false, playerUsage: false, takedown: false, thirdParty: false };
      }
      if (oldScreen === 'tags') {
        this.selectedTags = [];
        this.generatedTags = [];
        this.manualTagInput = '';
        this.tagError = '';
      }
    }

    // Cancel any in-progress loading if the user navigates away
    if (this.isLoading && newScreen !== oldScreen) {
      this.isLoading = false;
      this.loadingMessage = '';
      this.progress = 0;
    }

    next();
  },

  beforeRouteLeave(to, from, next) {
    if (this.cameraActive) this.closeCamera();
    next();
  },

  beforeDestroy() {
    // Clean up camera if still active
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
    }
    
    // Clean up GLB blob URL if exists
    if (this.glbBlobUrl) {
      URL.revokeObjectURL(this.glbBlobUrl);
    }

    this.releaseWakeLock();
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
  }
};
</script>

<style scoped>
.model-creator {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  padding: 40px 20px;
}

/* Selection Screen */
.selection-screen {
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  color: white;
}

.selection-screen h1 {
  font-size: 3em;
  margin-bottom: 10px;
}

.subtitle {
  font-size: 1.2em;
  opacity: 0.9;
  margin-bottom: 50px;
}

.method-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-top: 40px;
}

.method-card {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-radius: 20px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid #475569;
  transition: transform 0.3s, box-shadow 0.3s;
  position: relative;
  color: #f1f5f9;
}

.method-card.active {
  cursor: pointer;
}

.method-card.active:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 40px rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
}

.method-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.card-icon {
  font-size: 4em;
  margin-bottom: 20px;
}

.method-card h3 {
  font-size: 1.5em;
  margin-bottom: 10px;
}

.method-card p {
  color: #94a3b8;
  margin-bottom: 20px;
}

.coming-soon {
  display: inline-block;
  background: #ffd700;
  color: #333;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 0.9em;
  font-weight: bold;
}

.select-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.select-btn:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
  transform: translateY(-2px);
}

/* Test Mode Section */
.test-mode-section {
  margin-top: 50px;
  text-align: center;
}

.test-mode-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.3s;
}

.test-mode-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.test-input-group {
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-top: 20px;
  flex-wrap: wrap;
}

.test-input-group .task-id-input {
  flex: 1;
  max-width: 400px;
  min-width: 250px;
  padding: 12px 15px;
  border-radius: 10px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.3);
  color: white;
  font-size: 0.95em;
  font-family: 'Courier New', monospace;
}

.test-input-group .task-id-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.test-input-group .task-id-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.test-input-group .load-task-btn {
  padding: 12px 25px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 0.95em;
  cursor: pointer;
  transition: all 0.3s;
}

.test-input-group .load-task-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-1px);
}

.test-input-group .load-task-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Image Input Screen */
.image-input-screen {
  max-width: 800px;
  margin: 0 auto;
  color: white;
  text-align: center;
}

/* GLB Upload Screen */
.glb-upload-screen {
  max-width: 800px;
  margin: 0 auto;
  color: white;
  text-align: center;
}

.glb-upload-options {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 40px;
}

.glb-upload-screen .upload-area {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 2px dashed #475569;
  border-radius: 20px;
  padding: 50px 30px;
  cursor: pointer;
  transition: transform 0.3s, border-color 0.3s;
  color: #f1f5f9;
  margin-top: 0;
}

.upload-area--url {
  cursor: default !important;
}

.url-input-row {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  width: 100%;
}

.url-field {
  flex: 1;
  min-width: 0;
  padding: 10px 14px;
  border-radius: 10px;
  border: 1px solid #475569;
  background: rgba(15, 23, 42, 0.7);
  color: #f1f5f9;
  font-size: 0.9em;
  outline: none;
  transition: border-color 0.2s;
}

.url-field:focus {
  border-color: #3b82f6;
}

.url-field::placeholder {
  color: #64748b;
}

.url-load-btn {
  padding: 10px 18px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 0.9em;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
}

.url-load-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.url-load-btn:not(:disabled):hover {
  opacity: 0.85;
}

.glb-upload-screen .upload-area:not(.upload-area--url):hover {
  border-color: #3b82f6;
  transform: scale(1.02);
}

.glb-upload-screen .upload-area--url:hover {
  border-color: #3b82f6;
}

.file-preview {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 20px;
  padding: 40px;
  margin-top: 40px;
  color: #f1f5f9;
}

.obj-texture-row {
  margin-bottom: 20px;
}

.obj-texture-info {
  margin-bottom: 0;
}

.upload-area--small {
  padding: 16px 20px;
  margin-top: 0;
  border-radius: 12px;
  gap: 6px;
  font-size: 0.9em;
  cursor: pointer;
}

.upload-area--small span {
  font-size: 1.6em;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid #334155;
  border-radius: 12px;
}

.file-icon {
  font-size: 3em;
}

.file-details {
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 8px;
}

.file-details strong {
  font-size: 1.2em;
  color: #f1f5f9;
  word-break: break-word;
}

.file-details small {
  color: #94a3b8;
  font-size: 0.9em;
}

.back-btn {
  background: #334155;
  color: #f1f5f9;
  border: 1px solid #475569;
  padding: 10px 20px;
  border-radius: 20px;
  cursor: pointer;
  margin-bottom: 30px;
  font-size: 1em;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #475569;
}

/* Quality Preset Selector */
.quality-preset-selector {
  margin: 30px auto;
  max-width: 600px;
}

.quality-preset-selector label {
  display: block;
  font-size: 1.1em;
  font-weight: 600;
  margin-bottom: 15px;
  color: white;
}

.preset-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.preset-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 20px;
  color: white;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 1em;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.preset-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  transform: translateY(-2px);
}

.preset-btn.active {
  background: rgba(59, 130, 246, 0.4);
  border-color: #3b82f6;
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
}

.preset-btn small {
  font-size: 0.85em;
  opacity: 0.8;
  font-weight: normal;
}

.input-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-top: 40px;
}

.upload-area,
.camera-area {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 2px dashed #475569;
  border-radius: 20px;
  padding: 60px 30px;
  cursor: pointer;
  transition: transform 0.3s, border-color 0.3s;
  color: #f1f5f9;
}

.upload-area:hover,
.camera-area:hover {
  border-color: #3b82f6;
}

.upload-area:hover,
.camera-area:hover {
  transform: scale(1.05);
}

.upload-icon,
.camera-icon {
  font-size: 3em;
  margin-bottom: 20px;
}

.upload-area p,
.camera-area p {
  font-size: 1.2em;
  margin-bottom: 10px;
}

.upload-area small,
.camera-area small {
  color: #94a3b8;
}

/* Camera Preview */
.camera-preview {
  margin-top: 30px;
}

.camera-preview video {
  width: 100%;
  max-width: 640px;
  border-radius: 20px;
  margin-bottom: 20px;
}

.capture-btn,
.cancel-camera-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  padding: 15px 40px;
  border-radius: 25px;
  font-size: 1.1em;
  cursor: pointer;
  margin: 10px;
}

.cancel-camera-btn {
  background: #e74c3c;
}

/* Image Preview */
.image-preview {
  margin-top: 30px;
}

.image-preview img {
  max-width: 100%;
  max-height: 500px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  margin-bottom: 20px;
}

.preview-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.change-btn,
.create-btn {
  padding: 15px 40px;
  border-radius: 25px;
  font-size: 1.1em;
  cursor: pointer;
  border: none;
  transition: opacity 0.3s;
}

.change-btn {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.create-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.create-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Viewer Screen */
.viewer-screen {
  max-width: 1400px;
  margin: 0 auto;
  color: white;
}

.viewer-header {
  margin-bottom: 20px;
  text-align: center;
}

.viewer-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
  flex-wrap: wrap;
}

.download-btn,
.new-btn {
  padding: 12px 30px;
  border-radius: 25px;
  border: none;
  font-size: 1em;
  cursor: pointer;
}

.download-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.new-btn {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.url-text {
  max-width: 600px;
}

.copy-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  padding: 8px 15px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.9em;
}

.copy-btn:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.note {
  margin-top: 20px;
  padding: 15px;
  background: rgba(46, 204, 113, 0.2);
  border-left: 4px solid #2ecc71;
  border-radius: 5px;
  font-size: 0.95em;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  color: white;
}

.spinner {
  border: 5px solid rgba(255, 255, 255, 0.3);
  border-top: 5px solid white;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-message {
  margin-top: 20px;
  font-size: 1.3em;
}

.progress-container {
  width: min(400px, 80vw);
  height: 30px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  margin-top: 20px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 15px;
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
}

.progress-text {
  color: white;
  font-size: 0.9em;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Type / Visibility Screen */
.type-screen {
  max-width: 680px;
  margin: 0 auto;
}

.type-container {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

.type-title {
  color: #f1f5f9;
  font-size: 1.5em;
  margin: 0 0 8px;
}

.type-intro {
  color: #94a3b8;
  font-size: 0.95em;
  margin: 0 0 28px;
  line-height: 1.6;
}

.type-section {
  margin-bottom: 28px;
}

.type-section-title {
  color: #e2e8f0;
  font-size: 1em;
  font-weight: 600;
  margin: 0 0 12px;
}

.type-required {
  font-size: 0.8em;
  font-weight: 400;
  color: #64748b;
  margin-left: 6px;
}

.type-hint {
  color: #64748b;
  font-size: 0.85em;
  margin: -6px 0 10px;
}

.type-hint--indent {
  margin: 4px 0 12px 26px;
}

.type-radio-label,
.type-check-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #cbd5e1;
  font-size: 0.92em;
  margin-bottom: 10px;
  cursor: pointer;
  line-height: 1.5;
}

.type-radio-label input,
.type-check-label input {
  margin-top: 3px;
  flex-shrink: 0;
  accent-color: #3b82f6;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.type-select {
  width: 100%;
  background: #0f172a;
  border: 1px solid #475569;
  border-radius: 8px;
  color: #e2e8f0;
  padding: 10px 12px;
  font-size: 0.9em;
  cursor: pointer;
  outline: none;
}

.type-select:focus {
  border-color: #3b82f6;
}

.type-actions {
  margin-top: 28px;
}

.type-notice {
  margin-top: 12px;
  color: #475569;
  font-size: 0.8em;
  text-align: center;
}

.type-notice-link {
  color: #64748b;
  text-decoration: underline;
}

.type-notice-link:hover {
  color: #94a3b8;
}

/* Upload Complete Screen */
/* Tags Screen */
.tags-screen {
  max-width: 680px;
  margin: 0 auto;
}

.tags-container {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

.tags-title {
  color: #f1f5f9;
  font-size: 1.5em;
  margin: 0 0 8px;
}

.tags-intro {
  color: #94a3b8;
  font-size: 0.95em;
  margin: 0 0 6px;
  line-height: 1.6;
}

.tags-count {
  color: #64748b;
  font-size: 0.85em;
  margin: 0 0 20px;
}

.tags-input-row {
  display: flex;
  gap: 10px;
  margin: 16px 0 14px;
}

.tags-input {
  flex: 1;
  min-width: 0;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 10px;
  color: #e2e8f0;
  padding: 10px 14px;
  font-size: 0.9em;
}

.tags-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.tags-add-btn {
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-size: 0.88em;
  font-weight: 600;
  padding: 0 16px;
  cursor: pointer;
}

.tags-add-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.tag-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag-chip {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 20px;
  padding: 5px 8px 5px 12px;
  color: #cbd5e1;
  font-size: 0.85em;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.tag-chip:hover {
  border-color: #3b82f6;
  background: #1e293b;
}

.tag-chip-remove {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  max-width: 20px;
  max-height: 20px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid #475569;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  font-size: 0.85em;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tag-chip-remove:hover {
  border-color: #ef4444;
  color: #ef4444;
}

.tags-validation {
  color: #f59e0b;
  font-size: 0.82em;
  margin-bottom: 14px;
}

.tags-loading {
  color: #64748b;
  font-size: 0.9em;
  padding: 20px 0;
  text-align: center;
}

.tags-actions {
  margin-top: 4px;
}

/* Skin name field */
.skin-name-field {
  margin-bottom: 20px;
}

.skin-name-label {
  display: block;
  color: #94a3b8;
  font-size: 0.85em;
  font-weight: 600;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.skin-name-input {
  width: 100%;
  background: #0f172a;
  border: 1px solid #475569;
  border-radius: 10px;
  color: #f1f5f9;
  padding: 10px 14px;
  font-size: 1em;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.skin-name-input:focus {
  border-color: #3b82f6;
}

.skin-name-input::placeholder {
  color: #475569;
}

/* Categorize Screen */
.categorize-screen {
  max-width: 800px;
  margin: 0 auto;
  position: relative;
}

.categorize-container {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

/* Modifier Selection */
.modifier-selection {
  margin-top: 24px;
  padding: 20px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid #334155;
  border-radius: 12px;
}

.modifier-label {
  display: block;
  font-size: 1em;
  font-weight: 600;
  color: #e8eef5;
  margin-bottom: 12px;
}

.modifier-dropdown {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #2c3e50;
  border-radius: 10px;
  font-size: 1em;
  transition: all 0.3s ease;
  background: #1a2332;
  color: #ffffff;
  cursor: pointer;
  min-height: 120px;
}

.modifier-dropdown:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  background: #243447;
}

.modifier-dropdown option {
  background: #1a2332;
  color: #ffffff;
  padding: 8px;
}

.modifier-dropdown option:checked {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
}

.modifier-hint {
  display: block;
  margin-top: 8px;
  font-size: 0.85em;
  color: #94a3b8;
  font-style: italic;
}

.loading-modifiers {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 12px;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 8px;
  color: #94a3b8;
  font-size: 0.9em;
}

.spinner-small {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.categorize-actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

/* Conflict dialog */
.conflict-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 18, 30, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  z-index: 10;
}

.conflict-dialog {
  background: #1a2535;
  border: 1px solid #2c3e50;
  border-radius: 10px;
  padding: 28px 24px 20px;
  max-width: 380px;
  width: 90%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.conflict-title {
  color: #e8eef5;
  font-size: 0.95em;
  line-height: 1.5;
  margin: 0;
}

.conflict-hint {
  color: #7a8fa6;
  font-size: 0.82em;
  line-height: 1.5;
  margin: 0;
}

.conflict-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.conflict-thumb {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #2c3e50;
  background: #0d1520;
}

.conflict-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
}

.conflict-btn {
  border: none;
  border-radius: 6px;
  padding: 7px 18px;
  font-weight: bold;
  font-size: 0.88em;
  cursor: pointer;
  transition: background 0.15s;
}

.conflict-btn--cancel {
  background: #c0392b;
  color: #fff;
}

.conflict-btn--cancel:hover {
  background: #a93226;
}

.conflict-btn--override {
  background: #2471a3;
  color: #fff;
}

.conflict-btn--override:hover {
  background: #1a5276;
}

.conflict-btn--add {
  background: #2471a3;
  color: #fff;
}

.conflict-btn--add:hover {
  background: #1a5276;
}

.catalogue-only-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  color: #94a3b8;
  font-size: 0.9em;
  cursor: pointer;
  user-select: none;
}

.catalogue-only-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: #3b82f6;
  flex-shrink: 0;
  margin-top: 1px;
}

.catalogue-only-preview {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.catalogue-only-preview img {
  width: 256px;
  height: 256px;
  object-fit: contain;
  border-radius: 8px;
}

.back-btn {
  flex: 1;
  background: #334155;
  color: #f1f5f9;
  border: none;
  border-radius: 10px;
  padding: 14px 24px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-btn:hover {
  background: #475569;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.save-btn,
.add-model-btn {
  flex: 2;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 14px 24px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.save-btn:hover:not(:disabled),
.add-model-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.save-btn:disabled,
.add-model-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.viewer-screen .add-model-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 30px;
  font-size: 1em;
  font-weight: normal;
  cursor: pointer;
  transition: all 0.3s ease;
}

.viewer-screen .add-model-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
}

/* Screen navigation footer */
.screen-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 32px;
  padding: 0 4px;
}

.nav-btn {
  padding: 12px 32px;
  border-radius: 25px;
  border: none;
  font-size: 1em;
  cursor: pointer;
  transition: opacity 0.2s, background 0.2s;
}

.nav-btn--back {
  background: rgba(255, 255, 255, 0.2);
  color: white;
}

.nav-btn--back:hover {
  background: rgba(255, 255, 255, 0.32);
}

.nav-btn--next {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-weight: 600;
}

.nav-btn--next:hover:not(:disabled) {
  opacity: 0.85;
}

.nav-btn--next:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .selection-screen h1 {
    font-size: 2em;
  }
  
  .method-cards {
    grid-template-columns: 1fr;
  }
  
  .viewer-header {
    flex-direction: column;
    text-align: center;
  }

  .categorize-container {
    padding: 24px;
  }

  .categorize-actions {
    flex-direction: column;
  }
}

/* ── Text Prompt & Scan Miniature screens ── */
.text-input-screen,
.scan-input-screen {
  max-width: 620px;
  margin: 0 auto;
  padding: 20px;
}

.prompt-area {
  margin: 20px 0;
}

.prompt-input {
  width: 100%;
  min-height: 140px;
  padding: 14px;
  border: 1px solid #334155;
  border-radius: 10px;
  background: #0f172a;
  color: #f1f5f9;
  font-size: 15px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.prompt-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.char-count {
  display: block;
  text-align: right;
  color: #888;
  margin-top: 4px;
  font-size: 12px;
}

/* ── Scan slots ── */
.scan-slots {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin: 20px 0;
}

.scan-slot {
  border: 2px solid #334155;
  border-radius: 10px;
  overflow: hidden;
}

.scan-slot.required {
  border-color: #3b82f6;
}

.slot-label {
  background: #1e293b;
  color: #94a3b8;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.slot-hint {
  font-size: 10px;
  font-weight: 400;
  color: #666;
  font-style: italic;
}

.slot-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 110px;
  background: #0f172a;
  cursor: pointer;
  font-size: 36px;
  color: #475569;
  transition: background 0.2s, color 0.2s;
}

.slot-upload:hover {
  background: #1e293b;
  color: #94a3b8;
}

.slot-preview {
  position: relative;
  height: 110px;
}

.slot-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slot-clear {
  position: absolute;
  top: 5px;
  right: 5px;
  background: rgba(0, 0, 0, 0.75);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  padding: 0;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.scan-hint {
  color: #888;
  font-size: 13px;
  margin: 0 0 16px;
}

.scan-gallery-toggle {
  display: none;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 14px;
  margin: 12px 0 4px;
  cursor: pointer;
}

@media (pointer: coarse) {
  .scan-gallery-toggle {
    display: flex;
    justify-content: center;
  }
}
</style>
